import {useEffect, useState} from 'react';
import {Box, List, ListItemText} from '@mui/material';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SortableItem from './SortableItem';
import {ReactComponent as OrderIcon} from '../../../assests/svg/order_icon.svg';
import AurSingleSelectDropdown from '../../common/aur-single-select-dropdown/AurSingleSelectDropdown';
import {ArrayItemType, SortItemType} from '../eca-doc-list.util';
import {NullfieldOption, sortOptions} from '../ecaData';
import {not} from './ManageColFields';
import {HandleDrag} from './HandleDrag';
import {t} from 'i18next';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';

const isConfidenceScoreColumn = (column: string) => {
  return column === 'confidence_score';
};

const ManageSortOrder = ({
  selectedList,
  sortOrder,
  getSortOrder,
  includeSortOrderInEventName = false,
}: {
  selectedList: ArrayItemType[];
  sortOrder: SortItemType[];
  getSortOrder: (list: SortItemType[]) => void;
  includeSortOrderInEventName?: boolean;
}) => {
  const {trackGAEvent} = useAnalytics();

  const [sortableListOptions, setSortableListOptions] = useState<
    ArrayItemType[]
  >(selectedList.map((col) => ({...col, id: col.name})));
  const [items, setItems] = useState<SortItemType[]>(sortOrder);

  useEffect(() => {
    const selectedOp = sortOrder.map((i) => i.fieldValue);
    setSortableListOptions(not(selectedList, [...selectedOp]));
  }, []);

  useEffect(() => {
    getSortOrder(items);
  }, [items]);

  const handleSelectField = (
    selectedItem: ArrayItemType,
    itemId: string | number
  ) => {
    const currentItem = items.find((i) => i.id == itemId);
    const eventName = includeSortOrderInEventName
      ? `${selectedItem.label} - ${currentItem?.sortValue?.label ?? ''}`
      : selectedItem.label;

    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: eventName,
        type: GA_CLICK_TYPES.DROPDOWN,
        region: GA_REGIONS.DRAWER,
        action: t('gaTracking.ecaDocList.documentListSort'),
      },
      subPage: {
        subpage_name: t('gaTracking.ecaDocList.manageColumsSortOrder'),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
    setItems((prev: SortItemType[]) => {
      prev.filter((i) => i.id == itemId)[0].fieldValue = selectedItem;
      const filteredItems = prev.filter((i) => i.fieldValue.id != '0');
      const selectedOp = filteredItems.map((i) => i.fieldValue);
      setSortableListOptions(not(selectedList, [...selectedOp]));
      return [...prev];
    });
  };
  const handleSelectSort = (
    selectedItem: ArrayItemType,
    currentItem: SortItemType
  ) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: `${currentItem.fieldValue.label} - ${selectedItem.label}`,
        type: GA_CLICK_TYPES.DROPDOWN,
        region: GA_REGIONS.DRAWER,
        action: t('gaTracking.ecaDocList.documentListSort'),
      },
      subPage: {
        subpage_name: t('gaTracking.ecaDocList.manageColumsSortOrder'),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
    const item = {
      ...selectedItem,
      name: selectedItem.label?.toLowerCase(),
    };
    setItems((prev: SortItemType[]) => {
      prev.filter((i) => i.id === currentItem.id)[0].sortValue = item;
      return [...prev];
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <Box className="manageSortOrder-container">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(e: DragEndEvent) => {
          HandleDrag(e, setItems, {
            preventMoveAboveFieldName: 'confidence_score',
          });
        }}
      >
        <List className="sort-list">
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            {items.map((item, index) => (
              <SortableItem
                key={item.id}
                item={item}
                disabled={isConfidenceScoreColumn(item.fieldValue.name)}
                className="custom-list-item"
              >
                <>
                  <ListItemText className="custom-list-item-container">
                    <OrderIcon className="order-icon" aria-hidden="true" />
                    <Box className="field-dropdown">
                      <AurSingleSelectDropdown
                        selectedValue={item.fieldValue}
                        items={
                          item.fieldValue.id !== '0'
                            ? [
                                ...sortableListOptions,
                                item.fieldValue,
                                NullfieldOption,
                              ]
                            : [...sortableListOptions, item.fieldValue]
                        }
                        itemKey="id"
                        itemDisplay="label"
                        showDropDownLabel={false}
                        search={false}
                        data-testid={`field-${item.id}`}
                        onSelectionChange={(selectedItem) =>
                          handleSelectField(selectedItem, item.id)
                        }
                        disabled={isConfidenceScoreColumn(item.fieldValue.name)}
                        disablePortal={true}
                      />
                    </Box>
                    <Box className="sort-dropdown">
                      <AurSingleSelectDropdown
                        selectedValue={item.sortValue}
                        items={sortOptions}
                        itemKey="id"
                        itemDisplay="label"
                        showDropDownLabel={false}
                        search={false}
                        data-testid={`sort-${item.id}`}
                        onSelectionChange={(selectedItem) =>
                          handleSelectSort(selectedItem, item)
                        }
                        disabled={isConfidenceScoreColumn(item.fieldValue.name)}
                        disablePortal={true}
                      />
                    </Box>
                  </ListItemText>
                  {items.length - 1 !== index && (
                    <Box className="helper-tex-sort"> {t('common.note')}</Box>
                  )}
                </>
              </SortableItem>
            ))}
          </SortableContext>
        </List>
      </DndContext>
    </Box>
  );
};
export default ManageSortOrder;
